import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import AdminNavbar from "../Layout/AdminNavbar";
import Papa from "papaparse";
import API_BASE_URL from "../config";


export default function Holiday() {
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [weeklyHolidays, setWeeklyHolidays] = useState([]);
    const navigate = useNavigate();

    // modal state
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null); // null => add, otherwise holiday object

    // form state
    const emptyForm = { id: "", name: "", startDate: "", endDate: "", type: "PUBLIC", recurring: false, description: "" };
    const [form, setForm] = useState(emptyForm);

    // UI helpers
    const [query, setQuery] = useState("");
    const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
    const [filterType, setFilterType] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 10;


    // Fetch saved weekly holidays on load
    useEffect(() => {
    fetchWeeklyHolidays();
}, []);


// 🔐 Authentication check
  useEffect(() => {
  const token = localStorage.getItem("token");
  const roles = JSON.parse(localStorage.getItem("roles") || "[]");

  if (!token) {
    toast.error("You must be logged in to access this page.");
    navigate("/");
    return;
  }

  // ✅ check admin role
  if (!roles.includes("ROLE_ADMIN")) {
    toast.error("You are not authorized to access this page.");
    navigate("/");
    return;
  }
}, [navigate]);

async function fetchWeeklyHolidays() {
    try {
        const token = localStorage.getItem("token");

        const res = await axios.get(
            `${API_BASE_URL}/api/holidays/weekly`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        const days = res.data.map(h => h.name); // ✅ extract only names
        setWeeklyHolidays(days);
    } catch (err) {
        console.error("Failed to fetch weekly holidays:", err);
    }
}



// Toggle checkbox + auto-save immediately
async function toggleWeeklyHoliday(day) {
    try {
        const updated = weeklyHolidays.includes(day)
            ? weeklyHolidays.filter(d => d !== day)
            : [...weeklyHolidays, day];

        setWeeklyHolidays(updated);

        const token = localStorage.getItem("token");

        await axios.post(
            "/api/holidays/weekly",
            updated,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        console.log("✅ Weekly holidays updated:", updated);
    } catch (err) {
        console.error("❌ Failed to update weekly holidays:", err);
    }
}





    async function handleCSVUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async function (results) {
            try {
                const rows = results.data;

                // ✅ Validate format
                const validRows = rows.filter(
                    (r) => r.Name && r["Start Date"] && r["End Date"]
                );

                if (validRows.length === 0) {
                    alert("No valid records found in CSV!");
                    return;
                }

                // ✅ Transform CSV fields to match backend model
                const holidaysToUpload = validRows.map((r) => ({
                    name: r.Name.trim(),
                    startDate: r["Start Date"].trim(),
                    endDate: r["End Date"].trim(),
                    type: r.Type?.trim() || "PUBLIC",
                    recurring: r.Recurring?.toLowerCase() === "yes",
                    description: r.Description || "",
                }));

                const token = localStorage.getItem("token");

                // ✅ Send to backend
                for (const holiday of holidaysToUpload) {
                    await axios.post(
                        "/api/holidays",
                        holiday,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
                }

                alert(`✅ ${holidaysToUpload.length} holidays uploaded successfully!`);
                fetchHolidays();
            } catch (error) {
                console.error("Upload failed:", error);
                alert("Failed to upload holidays. Check console for details.");
            }
        },
    });
}



    useEffect(() => {
        fetchHolidays();
    }, []);

    async function fetchHolidays() {
    setLoading(true);
    setError("");
    try {
        const token = localStorage.getItem("token");

        const res = await axios.get(
            "/api/holidays",
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        setHolidays(
            Array.isArray(res.data) ? res.data : res.data.holidays || []
        );
    } catch (err) {
        console.error(err);
        setError("Failed to load holidays. Check console for details.");
    } finally {
        setLoading(false);
    }
}


    function openAdd() {
        setEditing(null);
        setForm(emptyForm);
        setShowModal(true);
    }

    function openEdit(h) {
        setEditing(h);
        setForm({
            id: h.id || "",
            name: h.name || "",
            startDate: (h.startDate || "").slice(0, 10),
            endDate: (h.endDate || "").slice(0, 10),
            type: h.type || "PUBLIC",
            recurring: !!h.recurring,
            description: h.description || "",
        });
        setShowModal(true);
    }


    function closeModal() {
        setShowModal(false);
        setEditing(null);
        setForm(emptyForm);
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

   async function handleSave(e) {
    e.preventDefault();
    try {
        const token = localStorage.getItem("token"); // 👈 from localStorage

        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };

        if (editing) {
            // update
            await axios.put(`/api/holidays/${editing.id}`, form, config);
        } else {
            // create
            await axios.post(`/api/holidays`, form, config);
        }

        await fetchHolidays();
        closeModal();
    } catch (err) {
        console.error(err);
        alert("Failed to save holiday. See console for details.");
    }
}


    async function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this holiday?")) return;

    try {
        const token = localStorage.getItem("token");

        await axios.delete(
            `/api/holidays/${id}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        setHolidays((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
        console.error(err);
        alert("Failed to delete holiday.");
    }
}


    // derived list (search + filters)
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return holidays
            .filter((h) => {
                // 🔍 Search by name or description
                if (q) {
                    const found =
                        (h.name || "").toLowerCase().includes(q) ||
                        (h.description || "").toLowerCase().includes(q);
                    if (!found) return false;
                }

                // 📅 Filter by year (use startDate)
                if (filterYear) {
                    const year = h.startDate ? h.startDate.slice(0, 4) : "";
                    if (year !== filterYear) return false;
                }

                // 🏷️ Filter by type
                if (filterType) {
                    if ((h.type || "").toUpperCase() !== filterType.toUpperCase())
                        return false;
                }

                return true;
            })
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    }, [holidays, query, filterYear, filterType]);


    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages]);

    const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

    function exportCSV() {
        const rows = ["Name,Start Date,End Date,Type,Recurring,Description"];

        filtered.forEach((h) => {
            const line = [
                `"${(h.name || "").replace(/"/g, '""')}"`,
                h.startDate ? h.startDate.slice(0, 10) : "",
                h.endDate ? h.endDate.slice(0, 10) : "",
                h.type || "",
                h.recurring ? "Yes" : "No",
                `"${(h.description || "").replace(/"/g, '""')}"`,
            ].join(",");
            rows.push(line);
        });
        const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `holidays_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    // helper to generate year options from data (or fallback range)
    const years = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const set = new Set();

        // Add years from data
        holidays.forEach(h => {
            if (h.startDate) set.add(h.startDate.slice(0, 4));
        });

        // Add a range (±2 years)
        for (let y = currentYear - 10; y <= currentYear + 2; y++) {
            set.add(String(y));
        }

        return Array.from(set).sort((a, b) => b - a);
    }, [holidays]);


    return (
        <div className="d-flex flex-column vh-100 bg-light">
            <div className="fixed-top"><AdminNavbar /></div>

            <div className="flex-grow-1 overflow-auto pt-5 p-4" style={{ marginTop: 80 }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mb-0">Holiday Master</h3>
                    <div className="d-flex gap-2">
                        <button className="btn btn-outline-secondary" onClick={fetchHolidays} title="Refresh">
                            Refresh
                        </button>

                        {/* Upload CSV */}
                        <label className="btn btn-outline-success ">
                            Upload CSV
                            <input
                                type="file"
                                accept=".csv"
                                hidden
                                onChange={handleCSVUpload}
                            />
                        </label>

                        <button className="btn btn-primary" onClick={openAdd}>
                            Add Holiday
                        </button>
                    </div>



                </div>

                <div className="card shadow-sm mb-4">
                    <div className="card-body">

                        <div className="row g-2 align-items-center">
                            <div className="col-md-3">
                                <input
                                    className="form-control"
                                    placeholder="Search by name or description..."
                                    value={query}
                                    onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                                />
                            </div>

                            <div className="col-md-2">
                                <select className="form-select" value={filterYear} onChange={(e) => { setFilterYear(e.target.value); setPage(1); }}>
                                    <option value="">All years</option>
                                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>

                            <div className="col-md-2">
                                <select className="form-select" value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(1); }}>
                                    <option value="">All types</option>
                                    <option value="PUBLIC">Public</option>
                                    <option value="NATIONAL">National</option>
                                    <option value="RELIGIOUS">Religious</option>
                                    <option value="FESTIVAL">Festival</option>
                                    <option value="OPTIONAL">Optional</option>
                                </select>
                            </div>

                            <div className="col-md-4 text-middle">
                                <button className="btn btn-outline-primary me-2" onClick={exportCSV}>Export CSV</button>
                            </div>
                        </div>
                    </div>

                    {/* 🗓️ Weekly Holidays Section */}

                    <div className="card-body">
                        <h5 className="mb-3">Weekly Holidays</h5>
                        <div className="d-flex align-items-center gap-4">
                            {["Saturday", "Sunday"].map((day) => (
                                <div className="form-check" key={day}>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={day}
                                        checked={weeklyHolidays.includes(day)}
                                        onChange={() => toggleWeeklyHoliday(day)}
                                    />
                                    <label className="form-check-label" htmlFor={day}>
                                        {day}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>



                </div>

                <div className="card shadow-sm">
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="p-5 text-center">Loading...</div>
                        ) : (
                            <div className="table-responsive" style={{ height: "49vh", overflowY: "auto" }}>
                                <table className="table table-hover mb-0 align-middle">
                                    <thead className="table-primary position-sticky top-0">
                                        <tr>
                                            <th style={{ width: 40 }}>Sr</th>
                                            <th style={{ width: 140 }}>Edit|Delete</th>
                                            <th style={{ width: 100 }}>Name </th>
                                            <th style={{ width: 210 }}>Date Range</th>
                                            <th style={{ width: 100 }}>Total Days</th> {/* ✅ New Column */}
                                            <th style={{ width: 120 }}>Type</th>
                                            <th style={{ width: 90 }}>Recurring</th>
                                            <th style={{ width: 210 }}>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageItems.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="text-center py-4 text-muted">
                                                    No holidays found.
                                                </td>
                                            </tr>
                                        ) : (
                                            pageItems.map((h, idx) => {
                                                // ✅ Calculate total days (inclusive)
                                                const start = new Date(h.startDate);
                                                const end = h.endDate ? new Date(h.endDate) : start;
                                                const totalDays =
                                                    Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1; // inclusive

                                                return (
                                                    <tr key={h.id || idx}>
                                                        <td>{(page - 1) * pageSize + idx + 1}</td>
                                                        <td>
                                                            <div className="d-flex gap-2 justify-content-start">
                                                                <span
                                                                    onClick={() => openEdit(h)}
                                                                    style={{
                                                                        color: "#0d6efd", // Bootstrap primary blue
                                                                        cursor: "pointer",
                                                                        fontWeight: "500",
                                                                        textDecoration: "none"
                                                                    }}
                                                                >
                                                                    Edit
                                                                </span>
                                                                <span
                                                                    onClick={() => handleDelete(h.id)}
                                                                    style={{
                                                                        color: "#dc3545", // Bootstrap danger red
                                                                        cursor: "pointer",
                                                                        fontWeight: "500",
                                                                        textDecoration: "none"
                                                                    }}
                                                                >
                                                                    Delete
                                                                </span>
                                                            </div>

                                                        </td>
                                                        <td>{h.name}</td>
                                                        <td>
                                                            {h.startDate
                                                                ? new Date(h.startDate).toLocaleDateString("en-GB", {
                                                                    day: "2-digit",
                                                                    month: "short",
                                                                    year: "numeric",
                                                                })
                                                                : ""}
                                                            {h.endDate
                                                                ? ` → ${new Date(h.endDate).toLocaleDateString("en-GB", {
                                                                    day: "2-digit",
                                                                    month: "short",
                                                                    year: "numeric",
                                                                })}`
                                                                : ""}
                                                        </td>

                                                        <td>{totalDays}</td> {/* ✅ Display total days */}
                                                        <td>{h.type}</td>
                                                        <td>{h.recurring ? "Yes" : "No"}</td>
                                                        <td style={{ maxWidth: 300 }}>{h.description}</td>

                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>

                            </div>
                        )}
                    </div>

                    <div className="card-footer d-flex justify-content-between align-items-center">
                        <div>Showing {filtered.length} result(s)</div>
                        <div>
                            <nav>
                                <ul className="pagination mb-0">
                                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button></li>
                                    {Array.from({ length: totalPages }).map((_, i) => (
                                        <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                                            <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}><button className="page-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button></li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>

                {/* Modal (simple inline modal implementation) */}
                {showModal && (
                    <>
                        <div
                            className="modal fade show"
                            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                            tabIndex="-1"
                            role="dialog"
                        >
                            <div className="modal-dialog modal-lg modal-dialog-centered">
                                <div className="modal-content shadow-lg border-0">
                                    <div className="modal-header" style={{ backgroundColor: "#607799", color: "rgba(253, 252, 252, 1)" }}>
                                        <h5 className="modal-title">
                                            {editing ? "Edit Holiday" : "Add Holiday"}
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close btn-close-white"
                                            onClick={closeModal}
                                        ></button>
                                    </div>

                                    <form onSubmit={handleSave}>
                                        <div className="modal-body">
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">Name</label>
                                                    <input
                                                        name="name"
                                                        value={form.name}
                                                        onChange={handleChange}
                                                        className="form-control"
                                                        required
                                                    />
                                                </div>

                                                <div className="col-md-3">
                                                    <label className="form-label">Start Date</label>
                                                    <input
                                                        name="startDate"
                                                        type="date"
                                                        value={form.startDate}
                                                        onChange={handleChange}
                                                        className="form-control"
                                                        required
                                                    />
                                                </div>

                                                <div className="col-md-3">
                                                    <label className="form-label">End Date</label>
                                                    <input
                                                        name="endDate"
                                                        type="date"
                                                        value={form.endDate}
                                                        min={form.startDate}
                                                        onChange={handleChange}
                                                        className="form-control"
                                                        required
                                                    />
                                                </div>


                                                <div className="col-md-4">
                                                    <label className="form-label">Type</label>
                                                    <select
                                                        name="type"
                                                        value={form.type}
                                                        onChange={handleChange}
                                                        className="form-select"
                                                    >
                                                        <option value="PUBLIC">Public</option>
                                                        <option value="NATIONAL">National</option>
                                                        <option value="RELIGIOUS">Religious</option>
                                                        <option value="FESTIVAL">Festival</option>
                                                        <option value="OPTIONAL">Optional</option>
                                                    </select>
                                                </div>

                                                <div className="col-md-4 d-flex align-items-center">
                                                    <div className="form-check mt-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="recurring"
                                                            id="recurring"
                                                            checked={form.recurring}
                                                            onChange={handleChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="recurring">
                                                            Recurring
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label">Description</label>
                                                    <textarea
                                                        name="description"
                                                        value={form.description}
                                                        onChange={handleChange}
                                                        className="form-control"
                                                        rows={3}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="modal-footer">
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={closeModal}
                                            >
                                                Cancel
                                            </button>
                                            <button type="submit" className="btn btn-primary">
                                                Save
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </>
                )}


            </div>
        </div>
    );
}
